// Settings for the LEAD-FACING "a broker has been assigned to you" email.
//
// Self-contained: it owns its own load, save, preview and test-send calls to
// the leadAssignedEmailAction callable and renders nothing outside its own
// card, so it can be dropped at the bottom of the Email page without touching
// the rest of it.
//
// The preview is SERVER-rendered and shown in a sandboxed iframe. Re-rendering
// the template in React here would drift from what the consumer actually
// receives, which is the one thing a preview must never do.
//
// Merge-field chips insert at the caret rather than appending, so an author
// can place {{brokerFirstName}} mid-sentence.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import {
  AlertTriangle,
  CheckCircle2,
  Eye,
  Loader2,
  Mail,
  Save,
  Send,
  ShieldAlert,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { functions } from '../../lib/firebase';
import { Skeleton } from '../common/SkeletonLoader';
// Browser-safe leaf; the schema barrel also exports server-only modules.
import {
  LEAD_ASSIGNED_MERGE_FIELDS,
  LEAD_ASSIGNED_MERGE_FIELD_LABELS,
  RESPONSE_TIMEFRAME_UNITS,
  formatResponseTimeframe,
  validateLeadAssignedEmailSettings,
  type LeadAssignedEmailMode,
  type LeadAssignedEmailSettings as Settings,
  type ResponseTimeframeUnit,
} from '@valutten/shared-schemas/dist/leadAssignedEmail';

const card = 'rounded-lg border border-gray-700 bg-gray-800/40 p-5';
const input =
  'w-full rounded-lg border border-gray-600 bg-gray-900 px-3 py-2 text-sm disabled:opacity-40';
const button =
  'inline-flex items-center justify-center gap-2 rounded-lg border border-gray-600 px-3 py-2 text-sm hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed';
const label = 'block text-xs font-semibold text-gray-300 mb-1';

const MODE_COPY: Record<LeadAssignedEmailMode, { title: string; body: string }> = {
  off: {
    title: 'Off',
    body: 'Nobody who enquires receives this email. Nothing is recorded.',
  },
  observe: {
    title: 'Preview only',
    body: 'Every assignment works out exactly which email it would send and records it, but nothing is delivered. Use this to watch the numbers for a few days before going live.',
  },
  enforce: {
    title: 'Live',
    body: 'The email is delivered to the person who enquired, from your own verified domain.',
  },
};

const UNIT_LABELS: Record<ResponseTimeframeUnit, string> = {
  minutes: 'minutes',
  hours: 'hours',
  business_hours: 'business hours',
  business_days: 'business days',
};

const EVENT_LABELS: Record<string, string> = {
  sent: 'Delivered',
  would_send: 'Would have been sent',
  failed_send: 'Failed to deliver',
  skipped_suppressed: 'Skipped, recipient unsubscribed',
  skipped_no_lead_email: 'Skipped, no email address on the lead',
  skipped_no_verified_domain: 'Skipped, your domain is not verified',
  skipped_no_unsubscribe_secret: 'Skipped, unsubscribe link unavailable',
  skipped_render_failed: 'Skipped, a merge field could not be filled',
  skipped_cross_tenant_recipient: 'Skipped, the assignee is outside your business',
  pending: 'In flight',
};

interface StatusResponse {
  mode: string;
  domainVerified: boolean;
  domain: string | null;
  windowDays: number;
  counts: Record<string, number>;
  total: number;
}

interface PreviewResponse {
  ok: boolean;
  subject?: string;
  html?: string;
  reason?: string;
  unresolved?: string[];
}

type Busy = null | 'saving' | 'testing';

interface Props {
  companyId?: string;
  /** False for a read-only viewer: fields render disabled, actions are hidden. */
  canManage: boolean;
}

export const LeadAssignedEmailSettings: React.FC<Props> = ({ companyId, canManage }) => {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [status, setStatus] = useState<StatusResponse | null>(null);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [previewAfterHours, setPreviewAfterHours] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<Busy>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const afterHoursBodyRef = useRef<HTMLTextAreaElement>(null);

  const call = useCallback(
    async <T,>(action: string, payload: Record<string, unknown> = {}): Promise<T> => {
      const fn = httpsCallable<Record<string, unknown>, T>(functions, 'leadAssignedEmailAction');
      const result = await fn({ companyId, action, ...payload });
      return result.data;
    },
    [companyId],
  );

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      call<{ settings: Settings }>('getSettings'),
      call<StatusResponse>('getStatus'),
    ])
      .then(([s, st]) => {
        if (cancelled) return;
        setSettings(s.settings);
        setStatus(st);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // Surfaced, never swallowed: a silent failure here reads as "the
        // feature is off" when it is actually a permissions or network problem.
        console.error('[LeadAssignedEmailSettings] load failed', err);
        setError(err instanceof Error ? err.message : 'Could not load these settings.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, call]);

  const problems = useMemo(
    () => (settings ? validateLeadAssignedEmailSettings(settings) : []),
    [settings],
  );

  const patch = (changes: Partial<Settings>): void => {
    setSettings((previous) => (previous ? { ...previous, ...changes } : previous));
    setNotice('');
  };

  const insertMergeField = (
    field: string,
    ref: React.RefObject<HTMLTextAreaElement>,
    current: string,
    apply: (next: string) => void,
  ): void => {
    const element = ref.current;
    const token = `{{${field}}}`;
    if (!element) {
      apply(`${current}${token}`);
      return;
    }
    const start = element.selectionStart ?? current.length;
    const end = element.selectionEnd ?? start;
    apply(`${current.slice(0, start)}${token}${current.slice(end)}`);
    requestAnimationFrame(() => {
      element.focus();
      element.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const refreshPreview = async (): Promise<void> => {
    try {
      setError('');
      const result = await call<PreviewResponse>('preview', { afterHours: previewAfterHours });
      setPreview(result);
    } catch (err: unknown) {
      console.error('[LeadAssignedEmailSettings] preview failed', err);
      setError(err instanceof Error ? err.message : 'Could not build the preview.');
    }
  };

  const save = async (): Promise<void> => {
    if (!settings) return;
    setBusy('saving');
    setError('');
    setNotice('');
    try {
      await call('saveSettings', { settings });
      setStatus(await call<StatusResponse>('getStatus'));
      setNotice('Saved.');
    } catch (err: unknown) {
      console.error('[LeadAssignedEmailSettings] save failed', err);
      setError(err instanceof Error ? err.message : 'Could not save these settings.');
    } finally {
      setBusy(null);
    }
  };

  const testSend = async (): Promise<void> => {
    setBusy('testing');
    setError('');
    setNotice('');
    try {
      const result = await call<{ to: string }>('testSend');
      setNotice(`Test email sent to ${result.to}.`);
    } catch (err: unknown) {
      console.error('[LeadAssignedEmailSettings] test send failed', err);
      setError(err instanceof Error ? err.message : 'Could not send the test email.');
    } finally {
      setBusy(null);
    }
  };

  if (!companyId) return null;

  if (loading) {
    return (
      <div className={card}>
        <Skeleton className="h-5 w-64 mb-4" />
        <Skeleton className="h-24 w-full mb-3" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (!settings) {
    return (
      <div className={card}>
        <div className="flex items-center gap-2 text-sm text-amber-300">
          <AlertTriangle className="w-4 h-4" />
          {error || 'These settings are unavailable.'}
        </div>
      </div>
    );
  }

  const domainBlocked = settings.mode === 'enforce' && status && !status.domainVerified;

  return (
    <div className={card}>
      <div className="flex items-center gap-2 mb-1">
        <Mail className="w-4 h-4 text-blue-400" />
        <h3 className="text-sm font-semibold text-white">
          Email the enquirer when a broker is assigned
        </h3>
      </div>
      <p className="text-xs text-gray-400 mb-4 leading-relaxed">
        When a new enquiry is routed to one of your brokers, this email tells the person who
        enquired who has it and when to expect a call. It is sent from your own verified domain,
        under your own branding.
      </p>

      {/* ── Status strip ─────────────────────────────────────────────── */}
      {status && (
        <div className="rounded-lg border border-gray-700 bg-gray-900/60 p-4 mb-5">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs">
            <span className="text-gray-400">
              Status:{' '}
              <span className="font-semibold text-white">
                {MODE_COPY[settings.mode].title}
              </span>
            </span>
            <span className="text-gray-400">
              Sending domain:{' '}
              {status.domainVerified ? (
                <span className="inline-flex items-center gap-1 font-semibold text-emerald-300">
                  <CheckCircle2 className="w-3 h-3" />
                  {status.domain} verified
                </span>
              ) : (
                <span className="font-semibold text-amber-300">not verified</span>
              )}
            </span>
            <span className="text-gray-400">
              Last {status.windowDays} days:{' '}
              <span className="font-semibold text-white">{status.total}</span>
            </span>
          </div>

          {!status.domainVerified && (
            <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200">
              <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold">No emails are being sent.</div>
                <div className="mt-0.5 leading-relaxed">
                  This email only ever goes out from your own verified domain, never from a
                  VALUTTEN address, so every send is being skipped until your domain is verified.{' '}
                  <Link to="/leads/setup" className="underline hover:text-amber-100">
                    Verify your domain in the Setup Wizard
                  </Link>
                  .
                </div>
              </div>
            </div>
          )}

          {Object.keys(status.counts).length > 0 && (
            <ul className="mt-3 space-y-1 text-xs text-gray-400">
              {Object.entries(status.counts)
                .sort((a, b) => b[1] - a[1])
                .map(([event, count]) => (
                  <li key={event} className="flex justify-between gap-4">
                    <span>{EVENT_LABELS[event] ?? event}</span>
                    <span className="font-semibold text-gray-200">{count}</span>
                  </li>
                ))}
            </ul>
          )}
        </div>
      )}

      {/* ── Mode ─────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        {(Object.keys(MODE_COPY) as LeadAssignedEmailMode[]).map((mode) => (
          <button
            key={mode}
            type="button"
            disabled={!canManage}
            onClick={() => patch({ mode })}
            className={`text-left rounded-lg border p-3 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
              settings.mode === mode
                ? 'border-blue-500 bg-blue-500/10'
                : 'border-gray-600 hover:bg-gray-700/40'
            }`}
          >
            <div className="text-xs font-semibold text-white">{MODE_COPY[mode].title}</div>
            <div className="mt-1 text-[11px] leading-relaxed text-gray-400">
              {MODE_COPY[mode].body}
            </div>
          </button>
        ))}
      </div>

      {/* ── Response timeframe ───────────────────────────────────────── */}
      <div className="mb-5">
        <span className={label}>What you promise the enquirer</span>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="number"
            min={1}
            max={365}
            disabled={!canManage || !!settings.responseTimeframe.label?.trim()}
            className={`${input} w-24`}
            value={settings.responseTimeframe.value}
            onChange={(e) =>
              patch({
                responseTimeframe: {
                  ...settings.responseTimeframe,
                  value: Math.max(1, Math.min(365, Number(e.target.value) || 1)),
                },
              })
            }
          />
          <select
            disabled={!canManage || !!settings.responseTimeframe.label?.trim()}
            className={`${input} w-48`}
            value={settings.responseTimeframe.unit}
            onChange={(e) =>
              patch({
                responseTimeframe: {
                  ...settings.responseTimeframe,
                  unit: e.target.value as ResponseTimeframeUnit,
                },
              })
            }
          >
            {RESPONSE_TIMEFRAME_UNITS.map((unit) => (
              <option key={unit} value={unit}>
                {UNIT_LABELS[unit]}
              </option>
            ))}
          </select>
        </div>
        <input
          type="text"
          maxLength={120}
          disabled={!canManage}
          placeholder="Or write it yourself, for example: by the end of the next business day"
          className={`${input} mt-2`}
          value={settings.responseTimeframe.label ?? ''}
          onChange={(e) =>
            patch({
              responseTimeframe: { ...settings.responseTimeframe, label: e.target.value },
            })
          }
        />
        <p className="mt-2 text-[11px] text-gray-500">
          The email will read: &ldquo;will be in touch {formatResponseTimeframe(settings.responseTimeframe)}&rdquo;.
          This is your public promise, and is separate from the internal window your team has
          before a lead reroutes.
        </p>
      </div>

      {/* ── Merge fields ─────────────────────────────────────────────── */}
      <div className="mb-3">
        <span className={label}>Merge fields (click to insert)</span>
        <div className="flex flex-wrap gap-1.5">
          {LEAD_ASSIGNED_MERGE_FIELDS.map((field) => (
            <button
              key={field}
              type="button"
              disabled={!canManage}
              title={LEAD_ASSIGNED_MERGE_FIELD_LABELS[field]}
              onClick={() =>
                insertMergeField(field, bodyRef, settings.bodyTemplate, (next) =>
                  patch({ bodyTemplate: next }),
                )
              }
              className="rounded-full border border-gray-600 px-2 py-0.5 text-[11px] text-gray-300 hover:bg-gray-700 disabled:opacity-40"
            >
              {`{{${field}}}`}
            </button>
          ))}
        </div>
      </div>

      {/* ── Copy ─────────────────────────────────────────────────────── */}
      <div className="mb-5 space-y-3">
        <div>
          <span className={label}>Subject</span>
          <input
            type="text"
            maxLength={200}
            disabled={!canManage}
            className={input}
            value={settings.subject}
            onChange={(e) => patch({ subject: e.target.value })}
          />
        </div>
        <div>
          <span className={label}>Message</span>
          <textarea
            ref={bodyRef}
            rows={10}
            maxLength={6000}
            disabled={!canManage}
            className={`${input} font-mono text-xs leading-relaxed`}
            value={settings.bodyTemplate}
            onChange={(e) => patch({ bodyTemplate: e.target.value })}
          />
          <p className="mt-1 text-[11px] text-gray-500">
            Leave a blank line between paragraphs. Your logo, business details and an unsubscribe
            link are added around this automatically.
          </p>
        </div>
      </div>

      {/* ── After hours ──────────────────────────────────────────────── */}
      <div className="mb-5 rounded-lg border border-gray-700 p-4">
        <label className="flex items-center gap-2 text-xs font-semibold text-gray-200">
          <input
            type="checkbox"
            disabled={!canManage}
            checked={settings.afterHours.enabled}
            onChange={(e) =>
              patch({ afterHours: { ...settings.afterHours, enabled: e.target.checked } })
            }
          />
          Use different wording for enquiries that arrive after hours
        </label>
        <p className="mt-1 text-[11px] text-gray-500">
          Used when the enquiry lands inside your quiet hours, or when a held enquiry is released
          the next morning.
        </p>
        {settings.afterHours.enabled && (
          <div className="mt-3 space-y-3">
            <div>
              <span className={label}>After-hours subject</span>
              <input
                type="text"
                maxLength={200}
                disabled={!canManage}
                className={input}
                value={settings.afterHours.subject ?? ''}
                onChange={(e) =>
                  patch({ afterHours: { ...settings.afterHours, subject: e.target.value } })
                }
              />
            </div>
            <div>
              <span className={label}>After-hours message</span>
              <textarea
                ref={afterHoursBodyRef}
                rows={8}
                maxLength={6000}
                disabled={!canManage}
                className={`${input} font-mono text-xs leading-relaxed`}
                value={settings.afterHours.bodyTemplate ?? ''}
                onChange={(e) =>
                  patch({ afterHours: { ...settings.afterHours, bodyTemplate: e.target.value } })
                }
              />
            </div>
          </div>
        )}
      </div>

      {/* ── Delivery options ─────────────────────────────────────────── */}
      <div className="mb-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <span className={label}>Send from</span>
          <select
            disabled={!canManage}
            className={input}
            value={settings.fromAddressMode}
            onChange={(e) =>
              patch({ fromAddressMode: e.target.value as 'broker' | 'noreply' })
            }
          >
            <option value="broker">The assigned broker, on your domain</option>
            <option value="noreply">A noreply address on your domain</option>
          </select>
        </div>
        <div>
          <span className={label}>Replies go to</span>
          <select
            disabled={!canManage}
            className={input}
            value={typeof settings.replyTo === 'object' ? 'custom' : settings.replyTo}
            onChange={(e) =>
              patch({
                replyTo:
                  e.target.value === 'custom'
                    ? { custom: '' }
                    : (e.target.value as 'broker' | 'company'),
              })
            }
          >
            <option value="broker">The assigned broker</option>
            <option value="company">Your business address</option>
            <option value="custom">Another address</option>
          </select>
          {typeof settings.replyTo === 'object' && (
            <input
              type="email"
              disabled={!canManage}
              placeholder="enquiries@yourbusiness.com.au"
              className={`${input} mt-2`}
              value={settings.replyTo.custom}
              onChange={(e) => patch({ replyTo: { custom: e.target.value } })}
            />
          )}
        </div>
      </div>

      <div className="mb-5 space-y-2 text-xs text-gray-300">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            disabled={!canManage}
            checked={settings.sendOnReassignment}
            onChange={(e) => patch({ sendOnReassignment: e.target.checked })}
          />
          Also email when an enquiry is moved to a different broker
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            disabled={!canManage}
            checked={settings.includeBrokerPhone}
            onChange={(e) => patch({ includeBrokerPhone: e.target.checked })}
          />
          Allow the broker&rsquo;s phone number to be used in the message
        </label>
      </div>

      {/* ── Problems ─────────────────────────────────────────────────── */}
      {problems.length > 0 && (
        <ul className="mb-4 space-y-1 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200">
          {problems.map((problem) => (
            <li key={problem}>{problem}</li>
          ))}
        </ul>
      )}
      {domainBlocked && (
        <div className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200">
          You can save this as live, but nothing will be delivered until your sending domain is
          verified.
        </div>
      )}
      {error && (
        <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-200">
          {error}
        </div>
      )}
      {notice && (
        <div className="mb-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs text-emerald-200">
          {notice}
        </div>
      )}

      {/* ── Actions ──────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={button} onClick={refreshPreview}>
          <Eye className="w-4 h-4" />
          Show preview
        </button>
        <label className="flex items-center gap-2 text-xs text-gray-400">
          <input
            type="checkbox"
            checked={previewAfterHours}
            onChange={(e) => setPreviewAfterHours(e.target.checked)}
          />
          Preview the after-hours version
        </label>
        {canManage && (
          <>
            <button
              type="button"
              className={button}
              disabled={busy !== null}
              onClick={testSend}
            >
              {busy === 'testing' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              Send a test to me
            </button>
            <button
              type="button"
              className={`${button} border-blue-500 bg-blue-600/20 hover:bg-blue-600/30`}
              disabled={busy !== null || problems.length > 0}
              onClick={save}
            >
              {busy === 'saving' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save
            </button>
          </>
        )}
      </div>

      {/* ── Preview ──────────────────────────────────────────────────── */}
      {preview && (
        <div className="mt-4">
          {preview.ok ? (
            <>
              <div className="text-xs text-gray-400 mb-1">
                Subject: <span className="text-gray-200">{preview.subject}</span>
              </div>
              <iframe
                title="Email preview"
                sandbox=""
                srcDoc={preview.html}
                className="w-full h-[32rem] rounded-lg border border-gray-700 bg-white"
              />
            </>
          ) : (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200">
              This email would not be sent: {preview.reason === 'broker_name_unresolved'
                ? 'the assigned broker has no name on their profile.'
                : `these merge fields have nothing to fill them: ${(preview.unresolved ?? []).join(', ')}.`}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LeadAssignedEmailSettings;
